import type { SchemaValidationErrorItem } from '@takeshape/errors';
import {
  type ProjectSchemaJSON,
  RuntimeSchema,
  type SchemaValidationResult,
  type UndefinedLayerStatus,
  UserSchema,
  validateSchema
} from '@takeshape/schema';
import { assert } from '@takeshape/util';
import { useDirtyBlock } from 'app/common/hooks/use-dirty-block';
import useRetry from 'app/common/hooks/use-retry';
import type { ProjectSchemaCompat } from 'app/compat/types';
import reduce from 'lodash/reduce';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compatSchemaToSchema } from '../../compat/utils.js';
import { getProjectEntitlements } from '../../project/selectors.js';
import type { LayerSchemaMap } from '../../shape/types.js';
import { clearScratchState, setUseScratchForPreview } from '../actions.js';
import { SCRATCH_BRANCH } from '../constants.js';
import { getCurrentBranchName, getUserSchema } from '../selectors.js';
import { selectIsDirty, useWorkingCopyStore } from '../store/working-copy-store.js';
import type { Layers } from '../types.js';
import { toServiceLayers } from '../utils.js';
import {
  getResolveQueryRef,
  getResolveShapeRef,
  type ResolveQueryRef,
  type ResolveShapeRef,
  useBranch
} from './use-branch';

export type UseActiveSchema = {
  activeSchemaJSON?: ProjectSchemaJSON;
  activeSchema?: UserSchema;
  /**
   * @deprecated use activeSchemaJSON instead
   */
  activeSchemaCompat?: ProjectSchemaCompat;
  dataOrigin: string | null;
  layerSchemaMap: LayerSchemaMap;
  resolveShapeRef: ResolveShapeRef;
  resolveQueryRef: ResolveQueryRef;
  loading: boolean;
  dirty: boolean;
  errors: SchemaValidationErrorItem[];
  setErrors: (value: SchemaValidationErrorItem[]) => void;
  hasPendingChanges: boolean;
  setHasPendingChanges: (value: boolean) => void;
  update: (newSchema: ProjectSchemaCompat, origin: string, andDeploy?: boolean) => Promise<void>;
  updating: boolean;
  save: () => void;
  saving: boolean;
  reset: () => void;
  validate: (newSchema: ProjectSchemaCompat | ProjectSchemaJSON) => Promise<SchemaValidationResult>;
  activeRuntimeSchema: RuntimeSchema | undefined;
  activeRuntimeSchemaJSON: ProjectSchemaJSON | undefined;
};

const dirtyConfig = {
  pathOnly: true,
  exemptPaths: [/^\/project\/.*\/api$/, /^\/project\/.*\/api\/(shape|agent|tool)\/.*$/]
};

export const WORKING_COPY_ORIGIN_USER_SCHEMA = 'WORKING_COPY_ORIGIN_USER_SCHEMA';

const layersToLayerSchemaMap = (layers: Layers): LayerSchemaMap => {
  return reduce(
    layers,
    (result, layer, key) => {
      if (key !== 'user' && layer.data) {
        result[key] = layer.data;
      }

      return result;
    },
    {} as LayerSchemaMap
  );
};

/**
 * The active schema is the one that is currently being previewed or edited.
 */
export function useActiveSchema(): UseActiveSchema {
  const dispatch = useDispatch();

  const userSchema = useSelector(getUserSchema);
  const currentBranchName = useSelector(getCurrentBranchName);
  const entitlements = useSelector(getProjectEntitlements);
  const currentBranch = useBranch(currentBranchName);
  const scratchBranch = useBranch(SCRATCH_BRANCH);

  const workingCopyCompat = useWorkingCopyStore((state) => state.workingCopyCompat);
  const workingCopyDataOrigin = useWorkingCopyStore((state) => state.dataOrigin);
  const workingCopyUserSchema = useWorkingCopyStore((state) => state.userSchema);
  const hasPendingChanges = useWorkingCopyStore((state) => state.hasPendingChanges);
  const errors = useWorkingCopyStore((state) => state.errors);
  const update = useWorkingCopyStore((state) => state.update);
  const dirty = useWorkingCopyStore((state) => {
    return selectIsDirty(state.workingCopyJSON ?? undefined, userSchema);
  });

  /** Resets the working copy and the preview branch. */
  const reset = useCallback(() => {
    useWorkingCopyStore.getState().reset();
    dispatch(clearScratchState());
    dispatch(setUseScratchForPreview(false));
  }, [dispatch]);

  const { setDirty } = useDirtyBlock({ ...dirtyConfig, onConfirm: reset });
  const { loading: loadingServiceLayers } = currentBranch.branch.serviceLayers;
  const { data: currentBranchCompat, loading: loadingUserLayer } = currentBranch.branch.layers.user;
  const { saving } = currentBranch.branch.runtimeSchema;
  const { saving: updating } = scratchBranch.branch.runtimeSchema;
  scratchBranch.branch.layers;

  const activeRuntimeSchemaJSON =
    scratchBranch.branch.runtimeSchema.data ?? currentBranch.branch.runtimeSchema.data ?? undefined;

  // TODO handle in store once branches are in zustand
  const scratchBranchRuntimeSchema = useMemo(() => {
    if (scratchBranch.branch.runtimeSchema.data) {
      return RuntimeSchema.fromJson(
        scratchBranch.branch.runtimeSchema.data,
        toServiceLayers(scratchBranch.branch.layers)
      );
    }
  }, [scratchBranch.branch.runtimeSchema.data, scratchBranch.branch.layers]);

  // TODO handle in store once branches are in zustand
  const currentBranchRuntimeSchema = useMemo(() => {
    if (currentBranch.branch.runtimeSchema.data) {
      return RuntimeSchema.fromJson(
        currentBranch.branch.runtimeSchema.data,
        toServiceLayers(currentBranch.branch.layers)
      );
    }
  }, [currentBranch.branch.runtimeSchema.data, currentBranch.branch.layers]);

  const activeRuntimeSchema = scratchBranchRuntimeSchema ?? currentBranchRuntimeSchema ?? undefined;

  // TODO handle in store once branches are in zustand
  const currentBranchUserSchema = useMemo(() => {
    if (currentBranch.branch.layers.user.data) {
      return UserSchema.fromJson(currentBranch.branch.layers.user.data, toServiceLayers(currentBranch.branch.layers));
    }
  }, [currentBranch.branch.layers.user.data, currentBranch.branch.layers]);

  const activeSchema = workingCopyUserSchema ?? currentBranchUserSchema ?? undefined;
  const activeSchemaCompat = workingCopyCompat ?? currentBranchCompat ?? undefined;
  const activeSchemaJSON = activeSchemaCompat ? compatSchemaToSchema(activeSchemaCompat) : undefined;
  const dataOrigin = workingCopyCompat
    ? workingCopyDataOrigin
    : currentBranchCompat
      ? WORKING_COPY_ORIGIN_USER_SCHEMA
      : null;

  /** Set dirty if the working copy diverges from the current user layer schema. */
  useEffect(() => {
    setDirty(dirty);
  }, [setDirty, dirty]);

  const setErrors = useCallback((value: SchemaValidationErrorItem[]) => {
    useWorkingCopyStore.getState().setWorkingCopyErrors(value);
  }, []);

  const setHasPendingChanges = useCallback((value: boolean) => {
    useWorkingCopyStore.getState().setWorkingCopyHasPendingChanges(value);
  }, []);

  // Prefer the scratch layers if loaded otherwise fall back to the current branch
  const layers = scratchBranch.branch.layers.user.data ? scratchBranch.branch.layers : currentBranch.branch.layers;

  const validate = useCallback(
    async (schema: ProjectSchemaCompat | ProjectSchemaJSON) => {
      assert(entitlements, 'no entitlements');
      return validateSchema(
        {
          async resolveLayer(id) {
            const layer = layers[id];
            if (!layer) {
              return { id, layer: undefined, status: 'disconnected' };
            }

            const { data: layerSchema, status } = layer;
            return layerSchema
              ? { id, schema: layerSchema, status: 'ok' }
              : {
                  id,
                  schema: undefined,
                  status: status as UndefinedLayerStatus
                };
          },
          entitlements
        },
        'version' in schema ? compatSchemaToSchema(schema) : schema
      );
    },
    [layers, entitlements]
  );

  /** Retries saving the schema until there are not pendingChanges */
  const retrySave = useRetry({
    ready: !hasPendingChanges,
    readyCallback: useCallback(() => {
      if (activeSchemaCompat) {
        currentBranch.updateSchema(activeSchemaCompat, 1000);
      }
    }, [activeSchemaCompat, currentBranch])
  });

  const layerSchemaMap = useMemo(() => {
    return layersToLayerSchemaMap(layers);
  }, [layers]);

  const resolveShapeRef = useMemo(() => getResolveShapeRef(activeSchemaCompat, layers), [activeSchemaCompat, layers]);

  const resolveQueryRef = useMemo(() => getResolveQueryRef(activeSchemaCompat, layers), [activeSchemaCompat, layers]);

  return {
    activeSchema,
    activeSchemaJSON,
    activeSchemaCompat,
    dataOrigin,
    layerSchemaMap,
    resolveShapeRef,
    resolveQueryRef,
    loading: loadingUserLayer || loadingServiceLayers,
    dirty,
    errors,
    setErrors,
    hasPendingChanges,
    setHasPendingChanges,
    update,
    updating,
    save: retrySave,
    saving,
    reset,
    validate,
    activeRuntimeSchema,
    activeRuntimeSchemaJSON
  };
}
